import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDownload, apiFetch } from "../../api/client";

// Reports are built server-side per viewer: access, redaction and every number
// arrive ready to render. `params` is part of each key because two periods are
// two different payloads.

const CATALOG_KEY = ["report-catalog"];
const SCHEDULES_KEY = ["report-schedules"];

export const useReportCatalog = () =>
  useQuery({
    queryKey: CATALOG_KEY,
    queryFn: () => apiFetch("/report-catalog").then((body) => body.items),
    staleTime: 5 * 60 * 1000,
  });

export const useReport = (reportId, params, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["report", reportId, params],
    queryFn: () => apiFetch(`/report-catalog/${reportId}`, { query: params }),
    enabled: Boolean(reportId) && enabled,
    staleTime: 60 * 1000,
  });

/** Fetch the PDF and hand it to the browser as a download. Web only: a blob
 *  download does nothing inside the iOS WebView, so native uses email instead. */
export const downloadReportPdf = async (reportId, params) => {
  const { blob, filename } = await apiDownload(`/report-catalog/${reportId}/pdf`, { query: params });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export const useDownloadReportPdf = () =>
  useMutation({ mutationFn: ({ reportId, params }) => downloadReportPdf(reportId, params) });

export const useEmailReport = () =>
  useMutation({
    mutationFn: ({ reportId, params, includeGuardian = false }) =>
      apiFetch(`/report-catalog/${reportId}/email`, {
        method: "POST",
        body: { params, include_guardian: includeGuardian },
        // Rendering + sending takes a few seconds; don't cut it at the default.
        timeoutMs: 60_000,
      }),
  });

export const useReportSchedules = () =>
  useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: () => apiFetch("/report-schedules").then((body) => body.items),
  });

const useScheduleMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
};

export const useCreateReportSchedule = () =>
  useScheduleMutation((body) => apiFetch("/report-schedules", { method: "POST", body, timeoutMs: 30_000 }));

export const useUpdateReportSchedule = () =>
  useScheduleMutation(({ id, ...body }) => apiFetch(`/report-schedules/${id}`, { method: "PATCH", body }));

export const useDeleteReportSchedule = () =>
  useScheduleMutation((id) => apiFetch(`/report-schedules/${id}`, { method: "DELETE" }));
